<template>
	<div class="parking-field">
		<EditableField
			v-if="readonly"
			type="text"
			name="parkingTypes"
			:title="t('transaction.parking.types')"
			:tooltip="tooltip"
			:value="selectionLabel"
			readonly
		/>
		<EditableField
			v-else
			type="select"
			name="parkingTypes"
			:title="t('transaction.parking.types')"
			:tooltip="tooltip"
			:options="options"
			placeholder=""
			:multiple="true"
			:value="selectValue"
			:readonly="readonly"
			:disabled="disabled"
			@input="onInput"
		/>
	</div>
</template>

<script setup lang="ts">
import { computed, shallowRef, watch } from "vue"
import { useI18n } from "vue-i18n"
import { TransactionParkingType } from "@/graphql"
import { displayedParkingTypes, normalizeParkingSelection } from "@/utils/transactionParking"
import EditableField from "./EditableField.vue"

const props = defineProps<{
	parkingTypes?: TransactionParkingType[] | null
	hasIndoorParking?: boolean | null
	hasOutdoorParking?: boolean | null
	readonly?: boolean
	disabled?: boolean
}>()
const emit = defineEmits<{ input: [field: { name: string; value: TransactionParkingType[] | null }] }>()
const { t } = useI18n()
const options = computed(() =>
	[
		TransactionParkingType.Indoor,
		TransactionParkingType.Outdoor,
		TransactionParkingType.None,
		TransactionParkingType.Unknown,
	].map(value => ({ value, label: t(`transaction.parking.${value}`) }))
)
const selection = computed(() =>
	displayedParkingTypes(props.parkingTypes, props.hasIndoorParking, props.hasOutdoorParking)
)
const selectValue = shallowRef(selection.value)
watch(selection, value => {
	selectValue.value = value
})
const selectionLabel = computed(() => selection.value.map(value => t(`transaction.parking.${value}`)).join(", "))
const presence = (value: boolean | null | undefined) =>
	t(value == null ? "transaction.parking.unknown" : value ? "transaction.card.yes" : "transaction.card.no")
const tooltip = computed(() =>
	[
		props.parkingTypes == null ? t("transaction.parking.inferred") : null,
		t("transaction.parking.summary", {
			indoor: presence(props.hasIndoorParking),
			outdoor: presence(props.hasOutdoorParking),
		}),
		!props.readonly ? t("transaction.parking.clearHint") : null,
	]
		.filter(Boolean)
		.join(" ")
)

function onInput({ value }: { value: TransactionParkingType[] | null }) {
	const parkingTypes = normalizeParkingSelection(selection.value, value ?? [])
	emit("input", { name: "parkingTypes", value: parkingTypes })
	if (parkingTypes == null && props.parkingTypes == null) {
		// Reset EditableField's local selection even when the null override is unchanged.
		selectValue.value = [...selection.value]
	}
}
</script>
